import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useCollectionBinding,
  useContentToken,
  usePropertyAddress,
  usePropertyAddressUrl,
  useSlotGroup,
  EditableCollectionBinding,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { addressLink, clean, hoursRows, stripHtml, telHref, type OfficeHour } from '../utils/quinn';
import { useRawProperty } from '../utils/hooks';
import { PATHS, tourCta, useSiteFlags } from '../utils/site';
import { SocialLinks } from '../utils/social';

const brandLogoDef: SlotGroupDefinition = {
  label: 'Company Logo',
  description: 'The company mark that leads the footer, in white on the property colour.',
  fields: {
    image: {
      label: 'Logo Image',
      type: 'image',
      defaultField: 'property_company_mono_white_logo',
      suggestedFields: ['property_company_mono_white_logo', 'property_company_logo'],
    },
  },
};

const privacyLinkDef: SlotGroupDefinition = {
  label: 'Privacy Policy Link',
  fields: {
    label: { label: 'Link Text', type: 'text', defaultValue: 'Privacy Policy' },
    url: { label: 'URL', type: 'url', defaultValue: '/privacy-policy' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const accessibilityLinkDef: SlotGroupDefinition = {
  label: 'Accessibility Link',
  fields: {
    label: { label: 'Link Text', type: 'text', defaultValue: 'Accessibility' },
    url: { label: 'URL', type: 'url', defaultValue: '/accessibility' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const sitemapLinkDef: SlotGroupDefinition = {
  label: 'Sitemap Link',
  fields: {
    label: { label: 'Link Text', type: 'text', defaultValue: 'Sitemap' },
    url: { label: 'URL', type: 'url', defaultValue: '/sitemap' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const quinnLogo = 'h-[78px] w-[176px] object-contain object-left max-[767px]:h-[58px] max-[767px]:w-[132px]';
const col =
  'flex flex-col gap-3 text-[15px] leading-[22px] max-[1365px]:w-auto max-[767px]:gap-0 max-[767px]:text-[14px] [&_a]:no-underline [&_a:hover]:underline max-[1199px]:[&_a]:block max-[1199px]:[&_a]:[padding:11px_0] max-[767px]:[&_.label]:mb-1';
const linkHit = 'max-[1199px]:[&_a]:inline-block max-[1199px]:[&_a]:[margin:-13px_0] max-[1199px]:[&_a]:[padding:13px_0]';

export function Footer() {
  const p = useRawProperty();
  const flags = useSiteFlags();
  const copyright = useContentToken('footer.copyright');
  const brand = useSlotGroup('footer.brand_logo', brandLogoDef);
  const privacy = useSlotGroup('footer.privacy_link', privacyLinkDef);
  const accessibility = useSlotGroup('footer.accessibility_link', accessibilityLinkDef);
  const sitemap = useSlotGroup('footer.sitemap_link', sitemapLinkDef);
  const hours = useCollectionBinding<OfficeHour>('footer.hours', { collection: 'office_hours' });

  const company = clean(p.property_company_name) || '';
  const companyUrl = clean(p.property_company_url);
  const companyHost = companyUrl ? companyUrl.replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/[/?#].*$/, '') : '';
  const address = clean(usePropertyAddress());
  const addressUrl = usePropertyAddressUrl();
  const tel = telHref(flags.phone);
  const tour = tourCta({}, flags.selfTourUrl);
  const rows = hoursRows(hours, !!flags.selfTourUrl);
  const needsMark = !!copyright && !stripHtml(copyright).startsWith('©');

  return (
    <footer className="bg-[--q-primary] px-[--gutter] pb-10 pt-28 text-[--q-on-primary] max-[767px]:pb-8 max-[767px]:pt-12 [&_a:focus-visible]:outline-[--q-accent-on-primary]">
      <div className="flex justify-between gap-10 max-[767px]:flex-col max-[767px]:flex-wrap max-[767px]:gap-8">
        <div className="flex w-80 shrink-0 flex-col gap-6 max-[1365px]:w-[260px] max-[767px]:w-auto max-[767px]:gap-3 min-[1366px]:max-[1439px]:w-[280px]">
          <EditableSlotGroup groupPath="footer.brand_logo" definition={brandLogoDef}>
            {brand.image ? (
              <VibesitesImage url={brand.image} alt={company} sizes="176px" className={quinnLogo} />
            ) : company ? (
              <div className={`flex items-center font-heading text-[26px] uppercase tracking-[.04em] ${quinnLogo}`}>{company}</div>
            ) : null}
          </EditableSlotGroup>
          <RichTextContent
            path="footer.name"
            as="div"
            className="text-[15px] leading-6 opacity-[.85] max-[767px]:text-[14px] max-[767px]:leading-[22px]"
          />
          <div
            className={`text-[15px] leading-6 opacity-[.85] max-[767px]:text-[14px] max-[767px]:leading-[22px] [&_a]:no-underline ${linkHit} min-[768px]:max-[1199px]:[&>span]:inline-block min-[768px]:max-[1199px]:[&>span]:max-w-[200px]`}
          >
            <RichTextContent path="footer.address" as="span" defaultValue={addressLink(address, addressUrl)} />
            {tel && (
              <>
                <br />
                <a className="dniphone dniphonehref" href={tel}>{flags.phone}</a>
              </>
            )}
          </div>
          <SocialLinks className="[&.social]:gap-2 [&.social_a]:h-11 [&.social_a]:w-11 [&.social_a]:border [&.social_a]:border-[color-mix(in_srgb,var(--q-on-primary)_25%,transparent)] [&.social_svg]:h-[18px] [&.social_svg]:w-[18px]" />
        </div>

        <div className="flex min-w-0 flex-1 justify-between gap-8 max-[1365px]:grid max-[1365px]:grid-cols-[1fr_1fr] max-[1365px]:gap-[36px_32px] max-[1199px]:flex-wrap max-[767px]:mt-2 max-[767px]:gap-[28px_20px] min-[768px]:max-[1365px]:[align-content:start]">
          <div className={`${col} w-[172px]`}>
            <div className="label label--on-primary">Homes</div>
            <a href={PATHS.floorPlans}>Floor Plans</a>
            {flags.shows('interactive-map') && <a href={PATHS.interactiveMap}>Interactive Map</a>}
            <a href={tour.href} data-vibesite-event={tour.isTour ? 'self_guided_tour_clicked' : undefined}>{tour.label}</a>
            {flags.applyUrl && <a href={flags.applyUrl} data-vibesite-event="apply_now_clicked">Apply Now</a>}
          </div>
          <div className={`${col} w-[172px]`}>
            <div className="label label--on-primary">Community</div>
            {flags.shows('amenities') && <a href={PATHS.amenities}>Amenities</a>}
            {flags.shows('gallery') && <a href={PATHS.gallery}>Gallery</a>}
            {flags.shows('virtual-tours') && <a href={PATHS.virtualTours}>Virtual Tours</a>}
            {flags.shows('pet-policy') && <a href={PATHS.petPolicy}>Pet Policy</a>}
            {flags.shows('neighborhood') && <a href={PATHS.neighborhood}>Neighborhood</a>}
            {flags.shows('reviews') && <a href={PATHS.reviews}>Reviews</a>}
          </div>
          <div className={`${col} w-[172px]`}>
            <div className="label label--on-primary">Leasing</div>
            {flags.shows('faqs') && <a href={PATHS.faqs}>FAQs</a>}
            {flags.shows('easy-fee-guide') && <a href={PATHS.feeGuide}>Easy Fee Guide</a>}
            <a href={PATHS.contact}>Contact Us</a>
            {flags.portalUrl && <a href={flags.portalUrl} data-vibesite-event="resident_portal_clicked">Resident Portal</a>}
          </div>
          <div
            className={`${col} w-[232px] max-[1199px]:[&_.hours>*>span]:whitespace-nowrap max-[767px]:[&_.hours]:grid max-[767px]:[&_.hours]:grid-cols-[auto_1fr] max-[767px]:[&_.hours]:items-baseline max-[767px]:[&_.hours]:gap-[6px_12px] max-[767px]:[&_.hours>*]:contents max-[767px]:[&_.hours>*]:flex-row max-[767px]:[&_.hours>*]:flex-wrap max-[767px]:[&_.hours>*]:justify-start max-[767px]:[&_.hours>*]:gap-[0_8px] min-[768px]:max-[1199px]:[&_.hours]:gap-2 min-[768px]:max-[1199px]:[&_.hours>*]:flex-col min-[768px]:max-[1199px]:[&_.hours>*]:gap-0 min-[1200px]:[&_.hours]:grid min-[1200px]:[&_.hours]:grid-cols-[max-content_max-content] min-[1200px]:[&_.hours]:gap-x-5 min-[1200px]:[&_.hours>*]:contents min-[1200px]:[&_.hours>*>span:first-child]:whitespace-nowrap min-[1200px]:[&_.hours>*>span:last-child]:text-right`}
          >
            <div className="label label--on-primary">Leasing office</div>
            <EditableCollectionBinding bindingPath="footer.hours" label="Office Hours">
              <div className="hours">
                {rows.length ? (
                  rows.map(([d, t]) => (
                    <div className="hours__row" key={d}>
                      <span>{d}</span>
                      <span>{t}</span>
                    </div>
                  ))
                ) : (
                  <div className="hours__row">
                    <a href={tour.href} data-vibesite-event={tour.isTour ? 'self_guided_tour_clicked' : undefined}>{tour.label}</a>
                  </div>
                )}
              </div>
            </EditableCollectionBinding>
          </div>
        </div>
      </div>

      <div className="mt-14 flex items-center justify-between gap-6 border-t border-t-[color-mix(in_srgb,var(--q-on-primary)_22%,transparent)] pt-6 text-[13px] leading-[18px] max-[1199px]:flex-col max-[1199px]:items-start max-[1199px]:gap-3 max-[767px]:mt-8 max-[767px]:gap-2.5 max-[767px]:pt-5 max-[767px]:text-[12px] min-[1200px]:max-[1365px]:flex-wrap min-[1200px]:max-[1365px]:gap-[12px_24px]">
        <div
          className={`flex gap-5 opacity-[.85] [&_a]:no-underline max-[1199px]:flex-wrap max-[1199px]:gap-[6px_20px] ${linkHit} max-[1199px]:[&_a]:whitespace-nowrap min-[768px]:max-[1199px]:[&>span]:whitespace-nowrap max-[767px]:gap-[4px_16px]`}
        >
          <span>
            {needsMark && '© '}
            <RichTextContent path="footer.copyright" as="span" />
          </span>
          <EditableSlotGroup groupPath="footer.privacy_link" definition={privacyLinkDef}>
            <a href={privacy.url || PATHS.privacy} target={privacy._target || '_self'}>{privacy.label || 'Privacy Policy'}</a>
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="footer.accessibility_link" definition={accessibilityLinkDef}>
            <a href={accessibility.url || PATHS.accessibility} target={accessibility._target || '_self'}>{accessibility.label || 'Accessibility'}</a>
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="footer.sitemap_link" definition={sitemapLinkDef}>
            <a href={sitemap.url || PATHS.sitemap} target={sitemap._target || '_self'}>{sitemap.label || 'Sitemap'}</a>
          </EditableSlotGroup>
        </div>
        <div
          className={`flex items-center gap-4 opacity-[.85] [&_a]:no-underline max-[1199px]:flex-wrap max-[1199px]:gap-[8px_16px] ${linkHit} max-[767px]:gap-[6px_12px] max-[767px]:text-[12px] max-[767px]:leading-[18px]`}
        >
          <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
            <path d="M11 3 3 9.5h2V19h12V9.5h2L11 3Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
            <path d="M7 12h8M7 15h8" stroke="currentColor" strokeWidth="1.5" />
          </svg>
          <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
            <circle cx="11" cy="4.5" r="1.8" stroke="currentColor" strokeWidth="1.5" />
            <path d="M9 8v6h6l2 5M9 10h5M6.5 12.5a4.5 4.5 0 1 0 7 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span className="[&_p]:m-0 [&_p]:inline">
            <RichTextContent path="footer.marks" as="span" />
            {companyHost && (
              <>
                {' · '}
                <a href={companyUrl!} target="_blank" rel="noopener">{companyHost}</a>
              </>
            )}
          </span>
        </div>
      </div>
    </footer>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Footer',
  displayName: 'Footer',
  tokens: {
    'footer.name': {
      label: 'Community Name',
      type: 'richtext',
      defaultField: 'property_name',
      defaultValue: '<p>Quinn Residences</p>',
    },
    'footer.address': {
      label: 'Address',
      description: 'Until edited, the address as a Google Maps link.',
      type: 'richtext',
      defaultValue: '<p><span data-type="field-token" data-field="property_full_address"></span></p>',
    },
    'footer.copyright': {
      label: 'Copyright',
      type: 'richtext',
      defaultField: 'property_copyright',
      defaultValue: '<p>All Rights Reserved.</p>',
    },
    'footer.marks': {
      label: 'Equal Housing Line',
      type: 'richtext',
      defaultValue:
        '<p>Equal housing opportunity · A <span data-type="field-token" data-field="property_company_name"></span> community</p>',
    },
  },
  slotGroups: {
    'footer.brand_logo': brandLogoDef,
    'footer.privacy_link': privacyLinkDef,
    'footer.accessibility_link': accessibilityLinkDef,
    'footer.sitemap_link': sitemapLinkDef,
  },
  collectionBindings: {
    'footer.hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(Footer as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default Footer;
